import { useSyncExternalStore } from 'react';

/**
 * #1060: a molecule made public or private again -- on its Monograph (a tab of its own) or from the
 * drawing share prompt -- updates the lock on its search cards in every open tab, without asking
 * the server again. The latest word in this tab wins over what a search said earlier.
 */
export type MoleculeVisibility = 'public' | 'private';

const CHANNEL = 'peruselab.molecule-visibility';
const known = new Map<string, MoleculeVisibility>();
const listeners = new Set<() => void>();

function apply(moleculeId: string, visibility: MoleculeVisibility) {
  known.set(moleculeId, visibility);
  for (const listener of listeners) listener();
}

let channel: BroadcastChannel | null = null;
function openChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  channel = new BroadcastChannel(CHANNEL);
  channel.onmessage = (event: MessageEvent<{ moleculeId?: unknown; visibility?: unknown }>) => {
    const { moleculeId, visibility } = event.data ?? {};
    if (typeof moleculeId === 'string' && (visibility === 'public' || visibility === 'private')) {
      apply(moleculeId, visibility);
    }
  };
  return channel;
}

/** Say that a molecule is now public or private, here and in the user's other tabs. */
export function announceMoleculeVisibility(moleculeId: string, visibility: MoleculeVisibility): void {
  apply(moleculeId, visibility);
  openChannel()?.postMessage({ moleculeId, visibility });
}

function subscribe(listener: () => void) {
  openChannel();
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The molecule's visibility as last announced, or undefined when nothing has been said. */
export function useAnnouncedMoleculeVisibility(moleculeId: string): MoleculeVisibility | undefined {
  return useSyncExternalStore(
    subscribe,
    () => known.get(moleculeId),
    () => undefined
  );
}
